import { useAuth, useUser } from '@clerk/clerk-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  buildTrackedAuthRedirectUrl,
  trackRewardedAdClaimFailed,
  trackRewardedAdClaimSucceeded,
  trackRewardedAdCtaClicked,
  trackRewardedAdStarted,
} from '../analytics';
import {
  claimRewardedAd,
  createRewardedAdIntent,
  getRewardedAdStatus,
} from '../api/ads';
import { useAds } from '../contexts/AdsContext';
import { useAlertQueueActions } from '../contexts/AlertQueueContext';
import { useGettoncini } from '../contexts/GettonciniContext';
import { queueResourceAlertsFromPayload } from '../utils/resourceAlerts';
import useHasEverAuthenticated from './useHasEverAuthenticated';

export const REWARDED_VIEW_STATES = {
  IDLE_READY: 'IDLE_READY',
  LOADING_AD: 'LOADING_AD',
  PLAYING: 'PLAYING',
  COOLDOWN: 'COOLDOWN',
  CAPPED_OR_UNAVAILABLE: 'CAPPED_OR_UNAVAILABLE',
};

const REWARDED_AD_BACKOFF_STORAGE_PREFIX = 'gdp_rewarded_ad_backoff_v1';

const RECOVERABLE_INTENT_ERRORS = new Set([
  'INTENT_NOT_FOUND_OR_RESTARTED',
  'INVALID_OR_EXPIRED_TOKEN',
]);

const REWARDED_AD_BACKOFF_DURATIONS_MS = {
  notReady: 15000,
  noAdPreloaded: 30000,
  frequencyCapped: 90000,
  timeout: 30000,
  error: 30000,
  other: 30000,
};

const UNAVAILABLE_BREAK_STATUSES = new Set(Object.keys(REWARDED_AD_BACKOFF_DURATIONS_MS));

const deriveViewStateFromStatus = (status) => {
  if (status?.state === 'ready' && status?.eligible) {
    return REWARDED_VIEW_STATES.IDLE_READY;
  }
  if (status?.state === 'cooldown') {
    return REWARDED_VIEW_STATES.COOLDOWN;
  }
  return REWARDED_VIEW_STATES.CAPPED_OR_UNAVAILABLE;
};

const isRecoverableIntentError = (errorCode) => RECOVERABLE_INTENT_ERRORS.has(String(errorCode || '').trim());

const getRewardedAdBackoffStorageKey = (sourceSurface, gameSlug) => (
  `${REWARDED_AD_BACKOFF_STORAGE_PREFIX}:${sourceSurface || 'rewarded'}:${gameSlug || 'global'}`
);

const readStoredRewardedAdBackoff = (storageKey) => {
  if (typeof window === 'undefined' || !window.localStorage || !storageKey) {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue);
    const breakStatus = String(parsed?.breakStatus || '').trim();
    const expiresAt = Number(parsed?.expiresAt);
    if (!breakStatus || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      window.localStorage.removeItem(storageKey);
      return null;
    }

    return {
      breakStatus,
      expiresAt,
    };
  } catch (_) {
    return null;
  }
};

const writeStoredRewardedAdBackoff = (storageKey, breakStatus) => {
  if (typeof window === 'undefined' || !window.localStorage || !storageKey) {
    return null;
  }

  const durationMs = REWARDED_AD_BACKOFF_DURATIONS_MS[breakStatus];
  if (!Number.isFinite(durationMs) || durationMs <= 0) {
    return null;
  }

  const value = {
    breakStatus,
    expiresAt: Date.now() + durationMs,
  };

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(value));
  } catch (_) {
    return null;
  }

  return value;
};

const clearStoredRewardedAdBackoff = (storageKey) => {
  if (typeof window === 'undefined' || !window.localStorage || !storageKey) {
    return;
  }

  try {
    window.localStorage.removeItem(storageKey);
  } catch (_) {
    // Local storage should stay non-blocking.
  }
};

const getRewardedAdBackoffMessage = (breakStatus) => {
  switch (breakStatus) {
    case 'frequencyCapped':
      return 'Hai raggiunto temporaneamente il limite annunci. Riprova più tardi.';
    case 'noAdPreloaded':
      return 'In questo momento non ci sono annunci premio pronti. Riprova tra poco.';
    case 'notReady':
      return 'Stiamo preparando gli annunci premio. Riprova tra poco.';
    case 'timeout':
      return 'L’annuncio premio ha impiegato troppo tempo a caricarsi. Riprova tra poco.';
    case 'error':
      return 'L’annuncio premio non è disponibile in questo momento. Riprova tra poco.';
    case 'other':
      return 'L’annuncio premio non è disponibile in questo momento. Riprova tra poco.';
    default:
      return 'L’annuncio premio non è disponibile in questo momento. Riprova più tardi.';
  }
};

export const useRewardedGettonciniAd = ({
  sourceSurface,
  gameSlug = null,
  authNextPath = null,
  signedOutGateReason = null,
  signedOutMessage = null,
} = {}) => {
  const { isSignedIn } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasEverAuthenticated = useHasEverAuthenticated();
  const {
    adsScriptStatus,
    adProviderType,
    requestRewardedAd,
  } = useAds();
  const { setGettonciniData } = useGettoncini();
  const { queueAlert } = useAlertQueueActions();
  const rewardedAdBackoffStorageKey = useMemo(
    () => getRewardedAdBackoffStorageKey(sourceSurface, gameSlug),
    [gameSlug, sourceSurface]
  );

  const [status, setStatus] = useState(null);
  const [viewState, setViewState] = useState(REWARDED_VIEW_STATES.LOADING_AD);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [lastErrorCode, setLastErrorCode] = useState(null);
  const [cooldownExpiresAtMs, setCooldownExpiresAtMs] = useState(null);
  const [cooldownNowMs, setCooldownNowMs] = useState(() => Date.now());
  const [cooldownDurationSeconds, setCooldownDurationSeconds] = useState(0);
  const [inventoryBackoff, setInventoryBackoff] = useState(() => (
    readStoredRewardedAdBackoff(getRewardedAdBackoffStorageKey(sourceSurface, gameSlug))
  ));

  const intentTokenRef = useRef(null);
  const beforeRewardErrorRef = useRef(null);
  const claimAttemptedRef = useRef(false);
  const claimSucceededRef = useRef(false);
  const rewardPlacementName = `${sourceSurface || 'rewarded'}_rewarded_gettoncini`;
  const nextPath = authNextPath || `${location.pathname}${location.search || ''}`;

  const applyRewardedAdBackoff = useCallback((breakStatus) => {
    const normalizedBreakStatus = String(breakStatus || '').trim();
    const nextBackoff = writeStoredRewardedAdBackoff(
      rewardedAdBackoffStorageKey,
      normalizedBreakStatus,
    );
    setInventoryBackoff(nextBackoff);
    if (nextBackoff) {
      setViewState(REWARDED_VIEW_STATES.CAPPED_OR_UNAVAILABLE);
      setFeedbackMessage(getRewardedAdBackoffMessage(normalizedBreakStatus));
      setLastErrorCode(normalizedBreakStatus.toUpperCase());
    }
    return nextBackoff;
  }, [rewardedAdBackoffStorageKey]);

  const clearRewardedAdBackoff = useCallback(() => {
    clearStoredRewardedAdBackoff(rewardedAdBackoffStorageKey);
    setInventoryBackoff(null);
  }, [rewardedAdBackoffStorageKey]);

  const activeInventoryBackoff = useMemo(() => {
    if (!inventoryBackoff) {
      return null;
    }
    if (inventoryBackoff.expiresAt <= Date.now()) {
      return null;
    }
    return inventoryBackoff;
  }, [inventoryBackoff]);

  useEffect(() => {
    setInventoryBackoff(readStoredRewardedAdBackoff(rewardedAdBackoffStorageKey));
  }, [rewardedAdBackoffStorageKey]);

  useEffect(() => {
    if (status?.state !== 'cooldown') {
      setCooldownExpiresAtMs(null);
      setCooldownDurationSeconds(0);
      return;
    }

    const cooldownSeconds = Number(status?.cooldown_seconds_remaining);
    if (!Number.isFinite(cooldownSeconds) || cooldownSeconds <= 0) {
      setCooldownExpiresAtMs(null);
      setCooldownDurationSeconds(0);
      return;
    }

    const now = Date.now();
    setCooldownNowMs(now);
    setCooldownDurationSeconds(cooldownSeconds);
    setCooldownExpiresAtMs(now + (cooldownSeconds * 1000));
  }, [status?.cooldown_seconds_remaining, status?.state]);

  const cooldownSecondsRemaining = useMemo(() => {
    if (status?.state !== 'cooldown') {
      const fallbackSeconds = Number(status?.cooldown_seconds_remaining);
      return Number.isFinite(fallbackSeconds) && fallbackSeconds > 0 ? fallbackSeconds : 0;
    }

    if (!cooldownExpiresAtMs) {
      const fallbackSeconds = Number(status?.cooldown_seconds_remaining);
      return Number.isFinite(fallbackSeconds) && fallbackSeconds > 0 ? fallbackSeconds : 0;
    }

    return Math.max(0, Math.ceil((cooldownExpiresAtMs - cooldownNowMs) / 1000));
  }, [cooldownExpiresAtMs, cooldownNowMs, status?.cooldown_seconds_remaining, status?.state]);

  const cooldownProgressPercent = useMemo(() => {
    if (status?.state !== 'cooldown') {
      return 0;
    }

    const duration = Number(cooldownDurationSeconds);
    if (!Number.isFinite(duration) || duration <= 0) {
      return 0;
    }

    return Math.max(0, Math.min(100, (cooldownSecondsRemaining / duration) * 100));
  }, [cooldownDurationSeconds, cooldownSecondsRemaining, status?.state]);

  const refreshStatus = useCallback(async () => {
    if (!isSignedIn) {
      setStatus(null);
      setViewState(REWARDED_VIEW_STATES.CAPPED_OR_UNAVAILABLE);
      return null;
    }

    setIsLoadingStatus(true);
    try {
      const token = await getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null);
      const nextStatus = await getRewardedAdStatus(token);
      setStatus(nextStatus);
      if (activeInventoryBackoff) {
        setViewState(REWARDED_VIEW_STATES.CAPPED_OR_UNAVAILABLE);
      } else if (adsScriptStatus === 'loading') {
        setViewState(REWARDED_VIEW_STATES.CAPPED_OR_UNAVAILABLE);
      } else {
        setViewState(deriveViewStateFromStatus(nextStatus));
      }
      setLastErrorCode(nextStatus?.error_code || null);
      if (activeInventoryBackoff && !claimSucceededRef.current) {
        setFeedbackMessage(getRewardedAdBackoffMessage(activeInventoryBackoff.breakStatus));
      } else if (adsScriptStatus === 'loading' && !claimSucceededRef.current) {
        setFeedbackMessage('Stiamo preparando gli annunci premio. Riprova tra poco.');
      } else if (!claimSucceededRef.current) {
        setFeedbackMessage(nextStatus?.message || '');
      }
      claimSucceededRef.current = false;
      return nextStatus;
    } catch (error) {
      const message = error?.response?.data?.detail || 'Annuncio premio non disponibile.';
      setStatus({
        success: false,
        state: 'unavailable',
        eligible: false,
        reward_amount: null,
        cooldown_seconds_remaining: 0,
        remaining_daily_claims: 0,
        max_daily_claims: 10,
        error_code: 'REWARDED_AD_STATUS_FAILED',
        message: typeof message === 'string' ? message : 'Annuncio premio non disponibile.',
      });
      setViewState(REWARDED_VIEW_STATES.CAPPED_OR_UNAVAILABLE);
      setLastErrorCode('REWARDED_AD_STATUS_FAILED');
      setFeedbackMessage('Annuncio premio non disponibile.');
      return null;
    } finally {
      setIsLoadingStatus(false);
    }
  }, [activeInventoryBackoff, adsScriptStatus, getToken, isSignedIn]);

  useEffect(() => {
    if (!cooldownExpiresAtMs) {
      return undefined;
    }

    const updateNow = () => {
      setCooldownNowMs(Date.now());
    };

    updateNow();
    const intervalId = window.setInterval(updateNow, 1000);
    const timeoutMs = Math.max(0, cooldownExpiresAtMs - Date.now());
    const timeoutId = window.setTimeout(() => {
      updateNow();
      refreshStatus();
    }, timeoutMs + 50);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [cooldownExpiresAtMs, refreshStatus]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    if (!activeInventoryBackoff) {
      return undefined;
    }

    const timeoutMs = Math.max(0, activeInventoryBackoff.expiresAt - Date.now());
    const timeoutId = window.setTimeout(() => {
      clearRewardedAdBackoff();
      refreshStatus();
    }, timeoutMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [activeInventoryBackoff, clearRewardedAdBackoff, refreshStatus]);

  useEffect(() => {
    if (!isSignedIn || !status) {
      return;
    }

    if (activeInventoryBackoff) {
      setViewState(REWARDED_VIEW_STATES.CAPPED_OR_UNAVAILABLE);
      if (!claimSucceededRef.current) {
        setFeedbackMessage(getRewardedAdBackoffMessage(activeInventoryBackoff.breakStatus));
        setLastErrorCode(activeInventoryBackoff.breakStatus.toUpperCase());
      }
      return;
    }

    if (adsScriptStatus === 'loading') {
      setViewState(REWARDED_VIEW_STATES.CAPPED_OR_UNAVAILABLE);
      if (!claimSucceededRef.current) {
        setFeedbackMessage('Stiamo preparando gli annunci premio. Riprova tra poco.');
        setLastErrorCode('ADS_LOADING');
      }
      return;
    }

    setViewState(deriveViewStateFromStatus(status));
    if (!claimSucceededRef.current && lastErrorCode === 'ADS_LOADING') {
      setFeedbackMessage(status?.message || '');
      setLastErrorCode(status?.error_code || null);
    }
  }, [activeInventoryBackoff, adsScriptStatus, isSignedIn, lastErrorCode, status]);

  const handleAuthClick = useCallback(() => {
    const authUrl = buildTrackedAuthRedirectUrl({
      cta_kind: hasEverAuthenticated ? 'sign_in' : 'sign_up',
      source_surface: sourceSurface,
      gate_reason: signedOutGateReason || `${sourceSurface}_rewarded_ads_requires_auth`,
      next_path: nextPath,
      message: signedOutMessage || 'Accedi per guardare un annuncio premio e ottenere gettoncini.',
      is_returning_device: hasEverAuthenticated,
      game_slug: gameSlug || null,
      track_gate_viewed: true,
      trigger: 'rewarded_ad_cta',
    });
    navigate(authUrl);
  }, [gameSlug, hasEverAuthenticated, navigate, nextPath, signedOutGateReason, signedOutMessage, sourceSurface]);

  const inventoryBlockReason = useMemo(() => {
    if (adProviderType !== 'H5') {
      return 'rewarded_not_supported';
    }
    if (adsScriptStatus === 'blocked') {
      return 'script_blocked';
    }
    if (typeof window === 'undefined' || typeof window.adBreak !== 'function') {
      return 'api_unavailable';
    }
    return null;
  }, [adProviderType, adsScriptStatus]);

  const handleClaimFailure = useCallback(async (error) => {
    const errorCode = error?.error_code || 'REWARDED_AD_CLAIM_FAILED';
    let message = error?.message || 'Non è stato possibile assegnare i gettoncini premio.';
    setLastErrorCode(errorCode);
    setFeedbackMessage(message);
    trackRewardedAdClaimFailed({
      source_surface: sourceSurface,
      game_slug: gameSlug,
      placement_name: rewardPlacementName,
      error_code: errorCode,
    });

    if (isRecoverableIntentError(errorCode)) {
      intentTokenRef.current = null;
      message = 'La richiesta premio è scaduta o il server è stato riavviato. Riprova.';
      setFeedbackMessage(message);
    }

    await refreshStatus();
    setLastErrorCode(errorCode);
    setFeedbackMessage(message);
  }, [gameSlug, refreshStatus, rewardPlacementName, sourceSurface]);

  const handleCtaClick = useCallback(async () => {
    if (!isSignedIn) {
      handleAuthClick();
      return { shown: false, reason: 'auth_required' };
    }

    if (inventoryBlockReason) {
      setViewState(REWARDED_VIEW_STATES.CAPPED_OR_UNAVAILABLE);
      setFeedbackMessage('Gli annunci premio non sono disponibili in questo momento.');
      setLastErrorCode(inventoryBlockReason.toUpperCase());
      return { shown: false, reason: inventoryBlockReason };
    }

    if (adsScriptStatus === 'loading') {
      setViewState(REWARDED_VIEW_STATES.CAPPED_OR_UNAVAILABLE);
      setFeedbackMessage('Stiamo preparando gli annunci premio. Riprova tra poco.');
      setLastErrorCode('ADS_LOADING');
      return { shown: false, reason: 'ads_loading' };
    }

    if (activeInventoryBackoff) {
      setViewState(REWARDED_VIEW_STATES.CAPPED_OR_UNAVAILABLE);
      setFeedbackMessage(getRewardedAdBackoffMessage(activeInventoryBackoff.breakStatus));
      setLastErrorCode(activeInventoryBackoff.breakStatus.toUpperCase());
      return { shown: false, reason: activeInventoryBackoff.breakStatus };
    }

    beforeRewardErrorRef.current = null;
    claimAttemptedRef.current = false;
    claimSucceededRef.current = false;
    intentTokenRef.current = null;
    setFeedbackMessage('');
    setLastErrorCode(null);
    setViewState(REWARDED_VIEW_STATES.LOADING_AD);
    trackRewardedAdCtaClicked({
      source_surface: sourceSurface,
      game_slug: gameSlug,
      placement_name: rewardPlacementName,
      reward_amount: status?.reward_amount || null,
    });

    const token = await getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null);

    const result = await requestRewardedAd({
      gameSlug,
      placementName: rewardPlacementName,
      sourceSurface,
      onBeforeReward: async ({ showAdFn }) => {
        try {
          const intent = await createRewardedAdIntent(
            {
              sourceSurface,
              gameSlug,
            },
            token,
          );
          intentTokenRef.current = intent.intent_token;
          await Promise.resolve(showAdFn());
        } catch (error) {
          beforeRewardErrorRef.current = error;
          setLastErrorCode(error?.error_code || 'REWARDED_AD_INTENT_FAILED');
          setFeedbackMessage(error?.message || 'Non è stato possibile preparare l\'annuncio premio.');
          throw error;
        }
      },
      onBeforeAd: () => {
        setViewState(REWARDED_VIEW_STATES.PLAYING);
        trackRewardedAdStarted({
          source_surface: sourceSurface,
          game_slug: gameSlug,
          placement_name: rewardPlacementName,
          provider_type: 'H5',
        });
      },
      onAdViewed: async () => {
        claimAttemptedRef.current = true;
        try {
          const response = await claimRewardedAd(intentTokenRef.current, token);
          if (response?.success && response?.app_resources?.gettoncini) {
            setGettonciniData(response.app_resources.gettoncini);
          }
          if (response?.success) {
            queueResourceAlertsFromPayload({
              queueAlert,
              payload: response,
              isAnonymous: false,
            });
          }
          claimSucceededRef.current = true;
          setFeedbackMessage(response?.message || 'Hai guadagnato gettoncini premio.');
          setLastErrorCode(null);
          trackRewardedAdClaimSucceeded({
            source_surface: sourceSurface,
            game_slug: gameSlug,
            placement_name: rewardPlacementName,
            reward_amount: response?.reward_amount || status?.reward_amount || null,
          });
          await refreshStatus();
        } catch (error) {
          await handleClaimFailure(error);
        } finally {
          intentTokenRef.current = null;
        }
      },
      onAdDismissed: () => {
        if (!claimAttemptedRef.current) {
          setViewState(REWARDED_VIEW_STATES.IDLE_READY);
          setFeedbackMessage('Annuncio interrotto prima del completamento.');
        }
      },
      onAdBreakDone: async ({ shown, rewardViewed, placementInfo }) => {
        const breakStatus = placementInfo?.breakStatus || null;

        if (!shown && UNAVAILABLE_BREAK_STATUSES.has(String(breakStatus || ''))) {
          applyRewardedAdBackoff(String(breakStatus || ''));
          await refreshStatus();
        }
      },
    });

    if (beforeRewardErrorRef.current) {
      setViewState(REWARDED_VIEW_STATES.CAPPED_OR_UNAVAILABLE);
      return result;
    }

    if (!result?.shown && !claimAttemptedRef.current) {
      if (UNAVAILABLE_BREAK_STATUSES.has(String(result?.placementInfo?.breakStatus || ''))) {
        applyRewardedAdBackoff(String(result?.placementInfo?.breakStatus || ''));
      } else if (status?.eligible) {
        setViewState(REWARDED_VIEW_STATES.IDLE_READY);
      }
    }

    return result;
  }, [
    activeInventoryBackoff,
    adsScriptStatus,
    applyRewardedAdBackoff,
    gameSlug,
    getToken,
    handleAuthClick,
    handleClaimFailure,
    inventoryBlockReason,
    isSignedIn,
    refreshStatus,
    requestRewardedAd,
    rewardPlacementName,
    queueAlert,
    setGettonciniData,
    sourceSurface,
    status?.eligible,
    status?.reward_amount,
  ]);

  return {
    cooldownSecondsRemaining,
    cooldownProgressPercent,
    feedbackMessage,
    handleAuthClick,
    handleCtaClick,
    inventoryBlockReason,
    isLoadingStatus,
    isSignedIn,
    lastErrorCode,
    maxDailyClaims: status?.max_daily_claims || 10,
    refreshStatus,
    remainingDailyClaims: status?.remaining_daily_claims || 0,
    rewardAmount: status?.reward_amount || null,
    status,
    viewState,
  };
};
